import { useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { capitalize, classnames } from "@/utils";
import BrandLogo from "@/components/BrandLogo";
import Container from "@/components/Container";
import AppIcon from "@/assets/friska-app-icon.svg?react";
import Platform from "@/components/Platform";
import useDevice from "@/hooks/useDevice";
import styles from "./Pages.module.scss";
import fallbackQrCodeSrc from "@/assets/onelink-qr-code.png";
import { STORAGE_KEY } from "@/constants";
import Spinner from "@/components/Spinner";
import { useAuth } from "@/hooks/useAuth";
import { getFlowConfig } from "@/data/flows";

const fallbackDownloadLink = "https://accumbo.page.link/get-started";

export default function AppDownload() {
  const { sessionId } = useParams();
  const [localSession, setLocalSession] = useState<BankIdPollingResponse>();
  const { session } = useAuth();
  const [fetchingSession, setFetchingSession] = useState(false);
  const [searchParams] = useSearchParams();
  const flowParam = searchParams.get("flow");

  const { getSession } = useApi();
  const { isAppleDevice, isMobileOrTablet } = useDevice();

  const flowConfig = flowParam ? getFlowConfig(flowParam) : undefined;

  useEffect(() => {
    setFetchingSession(true);
    if (localSession == null && sessionId) {
      getSession(sessionId)
        .then((res) => {
          localStorage.removeItem(STORAGE_KEY);
          return res.json();
        })
        .then(setLocalSession)
        .catch(console.log)
        .finally(() => setFetchingSession(false));
    }
  }, [localSession, sessionId]);

  let downloadLinkUrl =
    // @ts-ignore
    window.AF_SMART_SCRIPT_RESULT?.clickURL || fallbackDownloadLink;

  useEffect(() => {
    if (isMobileOrTablet) {
      return;
    }
    // @ts-ignore
    if (window.AF_SMART_SCRIPT) {
      try {
        // @ts-ignore
        window.AF_SMART_SCRIPT.displayQrCode("appsflyer-qr-code");
      } catch (e) {
        console.log(e);
      }
    }
  }, [isMobileOrTablet]);

  const username =
    session?.user?.givenName || localSession?.loginResponse?.user?.givenName;

  if (flowConfig?.appStoreUrls) {
    const downloadURL = new URL(downloadLinkUrl);

    if (flowConfig.appStoreUrls.ios) {
      downloadURL.searchParams.append(
        "af_ios_url",
        flowConfig.appStoreUrls.ios,
      );
    }
    if (flowConfig.appStoreUrls.android) {
      downloadURL.searchParams.append(
        "af_android_url",
        flowConfig.appStoreUrls.android,
      );
    }

    downloadLinkUrl = downloadURL.toString();
  }

  if (!sessionId && !session) {
    return <Navigate to={flowConfig?.landingPath || "/"} replace />;
  }

  if (!localSession && sessionId && fetchingSession) {
    return (
      <Container>
        <BrandLogo centered />
        <div className="flex justify-center mt-40">
          <Spinner size="large" />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <BrandLogo centered />
      <Platform desktop>
        <div className="flex justify-center mt-20 mb-20">
          {/* @ts-ignore */}
          {window.AF_SMART_SCRIPT ? (
            <div className={styles.qrWrapper} id="appsflyer-qr-code"></div>
          ) : (
            <div className={styles.qrWrapper}>
              <img src={fallbackQrCodeSrc} />
            </div>
          )}
        </div>
      </Platform>
      <Platform mobile>
        <div className={styles.appIconWrapper}>
          <a href={downloadLinkUrl} target="_blank">
            <AppIcon />
          </a>
        </div>
      </Platform>
      <h1 className="text-center">
        Vi fortsätter i appen
        {username ? `, ${capitalize(username)}` : ""}!
      </h1>
      <Platform desktop>
        <p className="text-center fw-500">
          Skanna QR-koden ovan med din mobil för att ladda ner appen och komma
          igång med din behandling.
        </p>
        <ul className="fw-500">
          <li>Enkel inloggning med BankID</li>
          <li>Du förbinder dig inte till något</li>
          <li>Avsluta när du vill</li>
        </ul>
      </Platform>
      <Platform mobile>
        <p className="text-center">
          Ladda ner appen för att prata med oss och komma ett steg närmare
          starten på din behandling.
        </p>
        <div className="text-center mt-40">
          <a
            href={downloadLinkUrl}
            className={classnames([
              styles.storeLink,
              isAppleDevice ? styles.apple : styles.google,
            ])}
          >
            Appnedladdning
          </a>
        </div>
      </Platform>
    </Container>
  );
}
